import { PlusOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, Tabs } from "antd";
import { type ReactNode, useEffect, useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useBrigadeStore } from "@/modules/brigades/store/brigade.store";
import { useDevicesStore } from "@/modules/devices/store/devices.store";
import { useEquipmentStore } from "@/modules/equipment/store/equipment.store";
import { useFaceEnrollmentStore } from "@/modules/face-enrollment/store/face-enrollment.store";
import { useGpsDevicesStore } from "@/modules/gps-devices/store/gps-devices.store";
import { useCrossAccessStore } from "@/modules/organization-cross-access/store/cross-access.store";
import { useOrgUsersStore } from "@/modules/organization-users/store/org-users.store";
import { useStaffStore } from "@/modules/staff/store/staff.store";
import { useTerritoriesStore } from "@/modules/territories/store/territories.store";
import { useUrlParam } from "@/shared/hooks/useUrlState";
import { OrganizationFormModal } from "../components/OrganizationFormModal";
import { OrgDetailHeader } from "../components/OrgDetailHeader";
import { OrgDetailInfoCard } from "../components/OrgDetailInfoCard";
import { buildOrgDetailTabs, type OrgDetailTabKey } from "../components/OrgDetailTabs";
import { ORGANIZATION_QUERY_KEY, useOrganization } from "../hooks/useOrganizations";
import { organizationService } from "../services/organization.service";
import { useOrganizationsStore } from "../store/organizations.store";

const CHILDREN_PAGE_LIMIT = 10;

export function OrganizationDetailPage() {
	const { id } = useParams<{ id: string }>();
	const queryClient = useQueryClient();
	// Tab lives in the URL so back/forward restores it; links to another org
	// carry no ?tab param, so a fresh org opens on "children" automatically.
	const [tabParam, setTabParam] = useUrlParam("tab", "children");

	// Prefetch children so "Quyi tashkilotlar" tab is instant when opened
	useEffect(() => {
		if (!id) {
			return;
		}
		const params = { page: 1, limit: CHILDREN_PAGE_LIMIT };
		queryClient.prefetchQuery({
			queryKey: [ORGANIZATION_QUERY_KEY, "children", id, params],
			queryFn: () => organizationService.listChildren(id, params),
		});
	}, [id, queryClient]);

	const { data: org, isLoading } = useOrganization(id);
	const openCreateOrgUser = useOrgUsersStore((s) => s.openCreateModal);
	const openCreateDevice = useDevicesStore((s) => s.openCreateModal);
	const openCreateCrossAccess = useCrossAccessStore((s) => s.openCreateModal);
	const openCreateStaff = useStaffStore((s) => s.openCreateModal);
	const openCreateBrigade = useBrigadeStore((s) => s.openCreateModal);
	const openCreateEquipment = useEquipmentStore((s) => s.openCreateModal);
	const openImportTerritory = useTerritoriesStore((s) => s.openImportModal);
	const openCreateFaceEnrollment = useFaceEnrollmentStore((s) => s.openAddModal);
	const openEditOrganization = useOrganizationsStore((s) => s.openEditModal);
	const openCreateChildOrg = useOrganizationsStore((s) => s.openCreateModal);
	const tabItems = useMemo(() => (id ? buildOrgDetailTabs(id) : []), [id]);
	const activeTab: OrgDetailTabKey = tabItems?.some((t) => t.key === tabParam)
		? (tabParam as OrgDetailTabKey)
		: "children";

	if (!id) {
		return <Navigate to="/organizations" replace />;
	}

	const tabActionMap: Partial<Record<OrgDetailTabKey, ReactNode>> = {
		children: id ? (
			<Button
				type="primary"
				icon={<PlusOutlined />}
				onClick={() => openCreateChildOrg({ parentId: id })}
				id="org-detail-add-child-btn"
			>
				Quyi tashkilot yaratish
			</Button>
		) : null,
		users: (
			<Button
				type="primary"
				icon={<PlusOutlined />}
				onClick={openCreateOrgUser}
				id="org-detail-add-user-btn"
			>
				Foydalanuvchi qo'shish
			</Button>
		),
		employees: (
			<Button
				type="primary"
				icon={<PlusOutlined />}
				onClick={openCreateStaff}
				id="org-detail-add-staff-btn"
			>
				Xodim qo'shish
			</Button>
		),
		devices: (
			<Button
				type="primary"
				icon={<PlusOutlined />}
				onClick={openCreateDevice}
				id="org-detail-add-device-btn"
			>
				Face id qo'shish
			</Button>
		),
		faceid: (
			<Button
				type="primary"
				icon={<PlusOutlined />}
				onClick={openCreateFaceEnrollment}
				id="org-detail-add-face-enrollment-btn"
			>
				Yuz qo'shish
			</Button>
		),
		"cross-access": id ? (
			<Button
				type="primary"
				icon={<PlusOutlined />}
				onClick={() => openCreateCrossAccess(id)}
				id="org-detail-add-cross-access-btn"
			>
				Kirish huquqini qo'shish
			</Button>
		) : null,
		brigades: id ? (
			<Button
				type="primary"
				icon={<PlusOutlined />}
				onClick={() => openCreateBrigade(id)}
				id="org-detail-add-brigade-btn"
			>
				Brigada qo'shish
			</Button>
		) : null,
		equipment: id ? (
			<Button
				type="primary"
				icon={<PlusOutlined />}
				onClick={() => openCreateEquipment(id)}
				id="org-detail-add-equipment-btn"
			>
				Texnika qo&apos;shish
			</Button>
		) : null,
		"gps-devices": id ? (
			<Button
				type="primary"
				icon={<PlusOutlined />}
				onClick={() => useGpsDevicesStore.getState().openCreateModal()}
				id="org-detail-add-gps-device-btn"
			>
				GPS qurilma qo&apos;shish
			</Button>
		) : null,
		territories: id ? (
			<Button
				type="primary"
				icon={<PlusOutlined />}
				onClick={() => openImportTerritory(id)}
				id="org-detail-import-territory-btn"
			>
				KMZ dan hudud import qilish
			</Button>
		) : null,
	};

	const tabItemsWithHeaders = tabItems?.map((tab) => {
		const action = tabActionMap[tab.key as OrgDetailTabKey];
		return {
			...tab,
			children: (
				<div className="min-h-[200px]">
					{action ? (
						<div className="flex items-center justify-end pt-6 pb-4 mb-4 border-b border-neutral-200 px-6">
							{action}
						</div>
					) : null}
					<div className={action ? "px-6 pb-6" : "px-6 py-6"}>{tab.children}</div>
				</div>
			),
		};
	});

	return (
		<div>
			<OrgDetailHeader
				org={org}
				isLoading={isLoading}
				onTitleClick={org ? () => openEditOrganization(org) : undefined}
			/>
			<OrgDetailInfoCard org={org} isLoading={isLoading} />

			<Card className="rounded-lg border-neutral-200 shadow-card" styles={{ body: { padding: 0 } }}>
				<Tabs
					className="org-detail-tabs"
					key={id}
					activeKey={activeTab}
					onChange={(key) => {
						if (key !== activeTab) {
							setTabParam(key);
						}
					}}
					items={tabItemsWithHeaders}
					size="middle"
					destroyOnHidden
					animated={false}
					tabBarStyle={{
						marginBottom: 0,
						paddingLeft: 24,
						paddingRight: 24,
						paddingTop: 8,
						paddingBottom: 12,
					}}
				/>
			</Card>

			<OrganizationFormModal />
		</div>
	);
}
